import React, { useState, useEffect, useMemo } from 'react';
import {
  PieChart as RcPieChart, Pie as RcPie, Cell as RcCell,
  BarChart as RcBarChart, Bar as RcBar,
  XAxis as RcXAxis, YAxis as RcYAxis,
  CartesianGrid as RcCartesianGrid,
  Tooltip as RcTooltip, Legend as RcLegend,
  ResponsiveContainer as RcResponsiveContainer,
} from 'recharts';
import { CHART_REGISTRY, aggregateByDimension, type DynamicChartConfig } from './ChartRegistry';
import DrillDownModal from './DrillDownModal';

const COLORS = ['#5a9b82','#3B82F6','#F59E0B','#EF4444','#8B5CF6','#14B8A6','#EC4899','#6366F1','#F97316','#06B6D4'];

interface Props {
  config: DynamicChartConfig;
  onRemove?: () => void;
  /** When true, shows a compact header (used inside the builder preview) */
  compact?: boolean;
}

export default function DynamicChart({ config, onRemove, compact = false }: Props) {
  const [chartData, setChartData] = useState<{ name: string; value: number }[]>([]);
  const [rawRows,   setRawRows]   = useState<Record<string, any>[]>([]);
  const [rawCols,   setRawCols]   = useState<{ id: string; label: string }[]>([]);
  const [totalRows, setTotalRows] = useState(0);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState<string | null>(null);
  const [drill, setDrill] = useState<{ open: boolean; loading: boolean; bucket: string; rows: Record<string, any>[] }>({ open: false, loading: false, bucket: '', rows: [] });

  const moduleDef   = CHART_REGISTRY.find(m => m.id === config.module);
  const dimLabel    = moduleDef?.dimensions.find(d => d.field === config.dimension)?.label || config.dimension;
  const activeFilters = config.filters.filter(f => f.value);
  const isRawTable  = config.chartType === 'raw-table';

  const filterValues = useMemo(() => {
    const fv: Record<string, string> = {};
    config.filters.forEach(f => { fv[f.field] = f.value; });
    return fv;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(config.filters)]);

  useEffect(() => {
    if (!moduleDef) { setLoading(false); setError('Unknown module'); return; }
    let cancelled = false;
    setLoading(true);
    setError(null);

    const load = async () => {
      if (isRawTable) {
        if (!moduleDef.rawFetcher) {
          setError('Raw table not available for this module');
          setLoading(false);
          return;
        }
        const { columns, rows } = await moduleDef.rawFetcher(filterValues, config.selectedFields ?? [], 500);
        if (cancelled) return;
        setRawCols(columns);
        setRawRows(rows);
        setTotalRows(rows.length);
        setChartData([]);
      } else if (moduleDef.backendFetcher) {
        const groups = await moduleDef.backendFetcher(filterValues, config.dimension);
        if (cancelled) return;
        setTotalRows(groups.reduce((s, d) => s + d.value, 0));
        setChartData(groups);
      } else {
        const rows = await moduleDef.fetcher(filterValues);
        if (cancelled) return;
        setTotalRows(rows.length);
        setChartData(aggregateByDimension(rows, config.dimension));
      }
      setLoading(false);
    };

    load().catch(() => {
      if (!cancelled) { setError('Failed to load data'); setLoading(false); }
    });

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.module, config.dimension, config.chartType, JSON.stringify(config.filters), JSON.stringify(config.selectedFields)]);

  const canDrill = !!moduleDef?.rawFetcher && !isRawTable;

  const handleDrill = async (bucketName: string) => {
    if (!moduleDef?.rawFetcher || !bucketName) return;
    setDrill({ open: true, loading: true, bucket: bucketName, rows: [] });
    try {
      const { rows } = await moduleDef.rawFetcher(filterValues, [], 500);
      const matched = rows.filter(r => {
        const val = r[config.dimension];
        const key = val !== null && val !== undefined && val !== '' ? String(val) : 'Unknown';
        return key === bucketName;
      });
      setDrill(p => ({ ...p, loading: false, rows: matched }));
    } catch {
      setDrill(p => ({ ...p, loading: false, rows: [] }));
    }
  };

  const drillColumns = (moduleDef?.allowedFields ?? []).map(f => ({ key: f.id, label: f.label }));

  // ── Render states ──────────────────────────────────────────────────────────
  const renderBody = () => {
    if (loading) return (
      <div style={{display:'flex', alignItems:'center', justifyContent:'center', height:180, flexDirection:'column', gap:8}}>
        <div style={{width:32, height:32, borderRadius:'50%', border:'3px solid #5a9b82', borderTopColor:'transparent', animation:'spin 0.8s linear infinite'}}/>
        <div style={{fontSize:12, color:'#9CA3AF'}}>Loading data…</div>
      </div>
    );

    if (error) return (
      <div style={{display:'flex', alignItems:'center', justifyContent:'center', height:180, flexDirection:'column', gap:6}}>
        <div style={{fontSize:24}}>⚠️</div>
        <div style={{fontSize:12, color:'#EF4444'}}>{error}</div>
      </div>
    );

    const isEmpty = isRawTable ? rawRows.length === 0 : chartData.length === 0;
    if (isEmpty) return (
      <div style={{display:'flex', alignItems:'center', justifyContent:'center', height:180, flexDirection:'column', gap:6}}>
        <div style={{fontSize:24}}>📭</div>
        <div style={{fontSize:12, color:'#9CA3AF'}}>No data for the selected filters</div>
      </div>
    );

    if (isRawTable) {
      const visibleCols = rawCols.length > 0 ? rawCols : (moduleDef?.allowedFields ?? []).map(f => ({ id: f.id, label: f.label }));
      return (
        <div style={{maxHeight:260, overflowY:'auto', overflowX:'auto', marginTop:8, borderRadius:6, border:'1px solid #F3F4F6'}}>
          <table style={{width:'100%', borderCollapse:'collapse', fontSize:12, tableLayout:'fixed', minWidth: visibleCols.length * 120}}>
            <thead style={{position:'sticky', top:0, background:'#F8FAFC', zIndex:1}}>
              <tr>
                {visibleCols.map(col => (
                  <th key={col.id} style={{padding:'7px 12px', textAlign:'left', color:'#6B7280', fontWeight:600, borderBottom:'1px solid #E5E7EB', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis'}}>
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rawRows.map((row, i) => (
                <tr key={i} style={{borderBottom:'1px solid #F9FAFB', background: i % 2 === 0 ? '#fff' : '#FAFAFA'}}>
                  {visibleCols.map(col => (
                    <td key={col.id} style={{padding:'6px 12px', color:'#374151', maxWidth:200, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>
                      {row[col.id] != null ? String(row[col.id]) : <span style={{color:'#D1D5DB'}}>—</span>}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    if (config.chartType === 'number') {
      const total = chartData.reduce((s, d) => s + d.value, 0);
      return (
        <div style={{display:'flex', alignItems:'center', justifyContent:'center', height:180, flexDirection:'column', gap:6}}>
          <div style={{fontSize:56, fontWeight:800, color:'#5a9b82', lineHeight:1}}>{total.toLocaleString()}</div>
          <div style={{fontSize:12, color:'#9CA3AF'}}>
            {totalRows.toLocaleString()} records · grouped by {dimLabel}
          </div>
        </div>
      );
    }

    if (config.chartType === 'table') {
      return (
        <div style={{maxHeight:220, overflowY:'auto', marginTop:8, borderRadius:6, border:'1px solid #F3F4F6'}}>
          <table style={{width:'100%', borderCollapse:'collapse', fontSize:12}}>
            <thead style={{position:'sticky', top:0, background:'#F8FAFC', zIndex:1}}>
              <tr>
                <th style={{padding:'7px 12px', textAlign:'left', color:'#6B7280', fontWeight:600, borderBottom:'1px solid #E5E7EB'}}>{dimLabel}</th>
                <th style={{padding:'7px 12px', textAlign:'right', color:'#6B7280', fontWeight:600, borderBottom:'1px solid #E5E7EB'}}>Count</th>
                <th style={{padding:'7px 12px', textAlign:'right', color:'#6B7280', fontWeight:600, borderBottom:'1px solid #E5E7EB'}}>%</th>
              </tr>
            </thead>
            <tbody>
              {chartData.map((row, i) => {
                const pct = totalRows > 0 ? Math.round((row.value / totalRows) * 100) : 0;
                return (
                  <tr key={i} style={{borderBottom:'1px solid #F9FAFB', cursor: canDrill ? 'pointer' : 'default'}}
                    onClick={() => canDrill && handleDrill(row.name)}>
                    <td style={{padding:'7px 12px', color:'#374151', display:'flex', alignItems:'center', gap:8}}>
                      <span style={{width:8, height:8, borderRadius:'50%', background:COLORS[i % COLORS.length], flexShrink:0, display:'inline-block'}}/>
                      {row.name}
                    </td>
                    <td style={{padding:'7px 12px', color:'#111827', textAlign:'right', fontWeight:600}}>{row.value}</td>
                    <td style={{padding:'7px 12px', textAlign:'right'}}>
                      <span style={{fontSize:11, color:'#9CA3AF'}}>{pct}%</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      );
    }

    if (config.chartType === 'donut') {
      return (
        <RcResponsiveContainer width="100%" height={210}>
          <RcPieChart>
            <RcPie data={chartData} cx="50%" cy="50%" innerRadius={55} outerRadius={80} paddingAngle={3} dataKey="value"
              onClick={(entry: any) => canDrill && handleDrill(entry?.name)} style={{cursor: canDrill ? 'pointer' : 'default'}}>
              {chartData.map((_, i) => <RcCell key={i} fill={COLORS[i % COLORS.length]} stroke="none"/>)}
            </RcPie>
            <RcTooltip formatter={(v: any, n: any) => [`${v} (${totalRows > 0 ? Math.round((v/totalRows)*100) : 0}%)`, n]} contentStyle={{fontSize:12, borderRadius:8, border:'1px solid #E5E7EB'}}/>
            <RcLegend iconType="circle" iconSize={9} wrapperStyle={{fontSize:11}}/>
          </RcPieChart>
        </RcResponsiveContainer>
      );
    }

    if (config.chartType === 'bar-v') {
      return (
        <RcResponsiveContainer width="100%" height={210}>
          <RcBarChart data={chartData} margin={{top:4, right:8, left:-10, bottom:36}}
            onClick={(data: any) => { const p = data?.activePayload?.[0]?.payload; if (canDrill && p?.name) handleDrill(p.name); }}
            style={{cursor: canDrill ? 'pointer' : 'default'}}>
            <RcCartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" vertical={false}/>
            <RcXAxis dataKey="name" tick={{fontSize:10, fill:'#6B7280'}} angle={-35} textAnchor="end" interval={0}/>
            <RcYAxis tick={{fontSize:10, fill:'#6B7280'}} allowDecimals={false}/>
            <RcTooltip contentStyle={{fontSize:12, borderRadius:8, border:'1px solid #E5E7EB'}}/>
            <RcBar dataKey="value" radius={[4,4,0,0]} maxBarSize={44}>
              {chartData.map((_, i) => <RcCell key={i} fill={COLORS[i % COLORS.length]}/>)}
            </RcBar>
          </RcBarChart>
        </RcResponsiveContainer>
      );
    }

    if (config.chartType === 'bar-h') {
      return (
        <RcResponsiveContainer width="100%" height={Math.max(180, chartData.length * 34)}>
          <RcBarChart data={chartData} layout="vertical" margin={{top:4, right:44, left:80, bottom:4}}
            onClick={(data: any) => { const p = data?.activePayload?.[0]?.payload; if (canDrill && p?.name) handleDrill(p.name); }}
            style={{cursor: canDrill ? 'pointer' : 'default'}}>
            <RcCartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" horizontal={false}/>
            <RcXAxis type="number" tick={{fontSize:10, fill:'#6B7280'}} allowDecimals={false}/>
            <RcYAxis type="category" dataKey="name" tick={{fontSize:10, fill:'#6B7280'}} width={75}/>
            <RcTooltip contentStyle={{fontSize:12, borderRadius:8, border:'1px solid #E5E7EB'}}/>
            <RcBar dataKey="value" radius={[0,4,4,0]} maxBarSize={24}
              label={{position:'right', fontSize:10, fill:'#6B7280', formatter:(v:any) => v}}>
              {chartData.map((_, i) => <RcCell key={i} fill={COLORS[i % COLORS.length]}/>)}
            </RcBar>
          </RcBarChart>
        </RcResponsiveContainer>
      );
    }

    return null;
  };

  return (
    <div className="card p-3" style={{borderRadius:10, height:'100%', position:'relative'}}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      {/* Header */}
      <div style={{display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:compact ? 4 : 6}}>
        <div style={{flex:1, minWidth:0}}>
          <div style={{fontSize: compact ? 13 : 14, fontWeight:600, color:'#374151', lineHeight:'20px', display:'flex', alignItems:'center', gap:6}}>
            <span>{moduleDef?.icon}</span>
            {config.label}
            {!loading && !error && (
              <span style={{fontSize:10, color:'#9CA3AF', fontWeight:400, marginLeft:2}}>
                ({totalRows.toLocaleString()} records)
              </span>
            )}
          </div>
          <div style={{fontSize:11, color:'#9CA3AF', marginTop:2, display:'flex', flexWrap:'wrap', gap:4}}>
            {!isRawTable && <span>by {dimLabel}</span>}
            {isRawTable && <span style={{fontSize:10, padding:'1px 7px', borderRadius:10, background:'#F3F4F6', color:'#6B7280', fontWeight:600}}>Raw Table</span>}
            {activeFilters.map((f, i) => (
              <span key={i} style={{padding:'1px 7px', borderRadius:10, background:'#EFF6FF', color:'#3B82F6', fontSize:10, fontWeight:600}}>
                {f.field}: {f.value}
              </span>
            ))}
          </div>
        </div>

        {onRemove && (
          <button onClick={onRemove}
            style={{background:'none', border:'none', cursor:'pointer', color:'#D1D5DB', fontSize:16, padding:'2px 4px', lineHeight:1, flexShrink:0, marginLeft:8}}
            title="Remove chart">✕</button>
        )}
      </div>

      {renderBody()}

      <DrillDownModal
        isOpen={drill.open}
        onClose={() => setDrill(p => ({ ...p, open: false }))}
        title={`${config.label} — ${drill.bucket}`}
        subtitle={`Records where ${dimLabel} = "${drill.bucket}"`}
        loading={drill.loading}
        columns={drillColumns}
        rows={drill.rows}
      />
    </div>
  );
}
